import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import {
    FiBell,
    FiEdit,
    FiGlobe,
    FiLock,
    FiSave,
    FiSettings,
    FiShield,
    FiUser
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import AuthService from '../../services/authService';

const Settings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    timezone: 'Asia/Kolkata',
    language: 'English'
  });

  // Load user data when component mounts
  useEffect(() => {
    if (user) {
      // Combine firstName and lastName for display, or use fullName if available
      const fullName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || '';
      
      setProfileData({
        name: fullName,
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'agent',
        timezone: user.preferences?.timezone || user.timezone || 'Asia/Kolkata',
        language: user.preferences?.language || user.language || 'English'
      });
    }
  }, [user]);

  const [notificationSettings, setNotificationSettings] = useState({
    newCarListings: true,
    newSellerRegistrations: true,
    salesNotifications: true,
    systemUpdates: false,
    marketingEmails: false,
    weeklyReports: true,
    monthlyReports: true
  });

  const [systemSettings, setSystemSettings] = useState({
    siteName: 'UsedAdda Agent Portal',
    siteDescription: 'Comprehensive car marketplace management',
    maintenanceMode: false,
    autoBackup: true,
    backupFrequency: 'daily',
    maxFileSize: '10',
    allowedFileTypes: 'jpg,jpeg,png,pdf,xlsx,xls'
  });

  const tabs = [
    { id: 'profile', name: 'Profile', icon: FiUser },
    { id: 'notifications', name: 'Notifications', icon: FiBell },
    { id: 'security', name: 'Security', icon: FiShield },
    { id: 'system', name: 'System', icon: FiSettings },
    { id: 'integrations', name: 'Integrations', icon: FiGlobe }
  ];

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      // Only send allowed fields that the backend accepts
      const allowedUpdates = {
        name: profileData.name,
        bio: profileData.bio || '',
        preferences: {
          timezone: profileData.timezone,
          language: profileData.language
        }
      };

      console.log('Updating profile with:', allowedUpdates);

      // Use AuthService which automatically handles token
      const result = await AuthService.updateProfile(allowedUpdates);
      console.log('Profile update response:', result);
      
      if (result.success) {
        toast.success('Profile updated successfully!');
        // Update local user data in localStorage
        if (result.data && result.data.user) {
          localStorage.setItem('agent_user', JSON.stringify(result.data.user));
        }
      } else {
        toast.error(result.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile.');
    }
  };

  const handleNotificationUpdate = (setting) => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
    toast.success('Notification settings updated!');
  };

  const handleSystemUpdate = (e) => {
    e.preventDefault();
    toast.success('System settings updated successfully!');
  };

  const renderProfileTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="bg-white rounded-2xl p-8 card-shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Profile Information</h3>
        <form onSubmit={handleProfileUpdate} className="space-y-6">
          <div className="flex items-center space-x-6">
            <div className="w-20 h-20 rounded-full gradient-bg flex items-center justify-center text-white text-2xl font-bold">
              {user?.name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="button"
                className="inline-flex items-center px-4 py-2 border border-orange-300 text-orange-700 bg-orange-50 font-medium rounded-lg hover:bg-orange-100 transition-all duration-200"
              >
                <FiEdit className="mr-2 h-4 w-4" />
                Change Avatar
              </motion.button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input
                type="text"
                value={profileData.name}
                onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <input
                type="text"
                value={profileData.role}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
              <select
                value={profileData.timezone}
                onChange={(e) => setProfileData(prev => ({ ...prev, timezone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                <option value="Europe/London">Europe/London (GMT)</option>
                <option value="America/New_York">America/New_York (EST)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
              <select
                value={profileData.language}
                onChange={(e) => setProfileData(prev => ({ ...prev, language: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="English">English</option>
                <option value="Hindi">Hindi</option>
                <option value="Tamil">Tamil</option>
                <option value="Telugu">Telugu</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="inline-flex items-center px-6 py-3 gradient-bg text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <FiSave className="mr-2 h-5 w-5" />
              Save Changes
            </motion.button>
          </div>
        </form>
      </div>
    </motion.div>
  );

  const renderNotificationsTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="bg-white rounded-2xl p-8 card-shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Notification Preferences</h3>
        <div className="space-y-6">
          {Object.entries(notificationSettings).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-900">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </p>
                <p className="text-sm text-gray-600">
                  {key === 'newCarListings' && 'Get notified when new cars are added'}
                  {key === 'newSellerRegistrations' && 'Get notified when new sellers register'}
                  {key === 'salesNotifications' && 'Get notified about successful sales'}
                  {key === 'systemUpdates' && 'Get notified about system maintenance and updates'}
                  {key === 'marketingEmails' && 'Receive marketing and promotional emails'}
                  {key === 'weeklyReports' && 'Receive weekly performance reports'}
                  {key === 'monthlyReports' && 'Receive monthly analytics reports'}
                </p>
              </div>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => handleNotificationUpdate(key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  value ? 'bg-orange-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    value ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </motion.button>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );

  const renderSecurityTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="bg-white rounded-2xl p-8 card-shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Security Settings</h3>
        <div className="space-y-8">
          {/* Change Password */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Change Password</h4>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter current password"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                className="inline-flex items-center px-4 py-2 gradient-bg text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <FiLock className="mr-2 h-4 w-4" />
                Update Password
              </motion.button>
            </form>
          </div>

          {/* Login Sessions */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Active Sessions</h4>
            <div className="space-y-4">
              {[
                { device: 'Chrome on Windows', location: 'Mumbai, India', time: 'Current session', current: true },
                { device: 'Safari on iPhone', location: 'Mumbai, India', time: '2 hours ago', current: false },
                { device: 'Firefox on Mac', location: 'Bangalore, India', time: '1 day ago', current: false },
              ].map((session, index) => (
                <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-900">{session.device}</p>
                    <p className="text-sm text-gray-600">{session.location} • {session.time}</p>
                  </div>
                  {!session.current && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Revoke
                    </motion.button>
                  )}
                  {session.current && (
                    <span className="text-green-600 text-sm font-medium">Current</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderSystemTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="bg-white rounded-2xl p-8 card-shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">System Configuration</h3>
        <form onSubmit={handleSystemUpdate} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
              <input
                type="text"
                value={systemSettings.siteName}
                onChange={(e) => setSystemSettings(prev => ({ ...prev, siteName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max File Size (MB)</label>
              <input
                type="number"
                value={systemSettings.maxFileSize}
                onChange={(e) => setSystemSettings(prev => ({ ...prev, maxFileSize: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Site Description</label>
            <textarea
              value={systemSettings.siteDescription}
              onChange={(e) => setSystemSettings(prev => ({ ...prev, siteDescription: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Allowed File Types</label>
            <input
              type="text"
              value={systemSettings.allowedFileTypes}
              onChange={(e) => setSystemSettings(prev => ({ ...prev, allowedFileTypes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="jpg,jpeg,png,pdf,xlsx,xls"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Backup Frequency</label>
            <select
              value={systemSettings.backupFrequency}
              onChange={(e) => setSystemSettings(prev => ({ ...prev, backupFrequency: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-900">Maintenance Mode</p>
                <p className="text-sm text-gray-600">Enable maintenance mode for system updates</p>
              </div>
              <motion.button
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={() => setSystemSettings(prev => ({ ...prev, maintenanceMode: !prev.maintenanceMode }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  systemSettings.maintenanceMode ? 'bg-orange-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    systemSettings.maintenanceMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </motion.button>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-900">Auto Backup</p>
                <p className="text-sm text-gray-600">Automatically backup system data</p>
              </div>
              <motion.button
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={() => setSystemSettings(prev => ({ ...prev, autoBackup: !prev.autoBackup }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  systemSettings.autoBackup ? 'bg-orange-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    systemSettings.autoBackup ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </motion.button>
            </div>
          </div>

          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="inline-flex items-center px-6 py-3 gradient-bg text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <FiSave className="mr-2 h-5 w-5" />
              Save Settings
            </motion.button>
          </div>
        </form>
      </div>
    </motion.div>
  );

  const renderIntegrationsTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="bg-white rounded-2xl p-8 card-shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Third-party Integrations</h3>
        <div className="space-y-6">
          {[
            { name: 'Payment Gateway', description: 'Razorpay integration for payments', status: 'connected', color: 'green' },
            { name: 'SMS Service', description: 'Twilio for SMS notifications', status: 'connected', color: 'green' },
            { name: 'Email Service', description: 'SendGrid for email delivery', status: 'connected', color: 'green' },
            { name: 'Google Analytics', description: 'Website analytics tracking', status: 'disconnected', color: 'red' },
            { name: 'AWS S3', description: 'Cloud storage for images', status: 'connected', color: 'green' },
          ].map((integration, index) => (
            <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
              <div className="flex items-center space-x-4">
                <div className={`w-3 h-3 rounded-full ${integration.color === 'green' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <div>
                  <p className="font-medium text-gray-900">{integration.name}</p>
                  <p className="text-sm text-gray-600">{integration.description}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  integration.status === 'connected' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {integration.status}
                </span>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                >
                  Configure
                </motion.button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-gray-600">Manage your account and system preferences</p>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl p-2 card-shadow"
      >
        <nav className="flex space-x-2">
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'gradient-bg text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <tab.icon className="mr-2 h-4 w-4" />
              {tab.name}
            </motion.button>
          ))}
        </nav>
      </motion.div>

      {/* Tab Content */}
      <div>
        {activeTab === 'profile' && renderProfileTab()}
        {activeTab === 'notifications' && renderNotificationsTab()}
        {activeTab === 'security' && renderSecurityTab()}
        {activeTab === 'system' && renderSystemTab()}
        {activeTab === 'integrations' && renderIntegrationsTab()}
      </div>
    </div>
  );
};

export default Settings;